import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, IconButton, MobileStepper, Fade } from '@mui/material';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';

const steps = [
  {
    label: 'Global Expertise',
    description:
      'We are a global consulting firm helping organizations protect and enhance enterprise value in an increasingly complex legal, regulatory and economic environment.',
    image: '1.jpg',
  },
  {
    label: 'Strategic Guidance',
    description:
      'Our experts deliver clarity and insight to businesses navigating critical challenges, enabling confident decision-making across industries.',
    image: '2.jpg',
  },
  {
    label: 'Innovative Solutions',
    description:
      'Through strategic use of data, technology, and industry knowledge, we create tailored solutions that deliver measurable impact.',
    image: 'https://images.unsplash.com/photo-1573497491208-6b1acb260507?auto=format&fit=crop&w=800&q=80',
  },
];

const IntroSection: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const maxSteps = steps.length;
  const [fadeIn, setFadeIn] = useState(true);

  const nextStep = () => {
    setFadeIn(false);
    setTimeout(() => {
      setActiveStep((prevStep) => (prevStep + 1) % maxSteps);
      setFadeIn(true);
    }, 200);
  };

  const prevStep = () => {
    setFadeIn(false);
    setTimeout(() => {
      setActiveStep((prevStep) => (prevStep - 1 + maxSteps) % maxSteps);
      setFadeIn(true);
    }, 200);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      nextStep();
    }, 3000);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, []);

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100vw',
        minHeight: { xs: 420, sm: 500, md: '90vh' },
        maxHeight: { xl: '1080px' },
        overflow: 'hidden',
       
        // Change background to dark blue:
        backgroundColor: '#002b54',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pb: { xs: 2, md: 4, xl: 0 },
      }}
    >
      <Fade in={fadeIn} timeout={500}>
        <Paper
          square
          elevation={3}
          sx={{
            // Make Paper full width on large screens:
            maxWidth: { xs: '98%', md: 1200, lg: '100vw', xl: '100vw' },
            width: { xs: '98%', sm: '94%', lg: '100vw', xl: '100vw' },
            mx: { xs: 'auto', lg: 0, xl: 0 },
            my: 'auto',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            p: { xs: 2, md: 4, xl: 5 },
            backgroundColor: '#002b54',
            boxShadow: { xs: 0, md: 3 },
            borderRadius: 0,
          }}
        >
          <Box
            component="img"
            src={steps[activeStep].image}
            alt={steps[activeStep].label}
            sx={{
              width: { xs: '100%', md: '45%', xl: '44%' },
              height: { xs: 180, sm: 250, md: 350, lg: 390, xl: 460 },
              objectFit: 'cover',
              borderRadius: 0,
              mr: { md: 3, xl: 5 },
              mb: { xs: 2, md: 0 },
              boxShadow: { xs: 0, md: 2 },
              maxHeight: { xl: 460 },
            }}
          />
          <Box sx={{ flex: 1, px: { xs: 1, md: 3, xl: 5 }, py: { xs: 2, md: 0 } }}>
            <Typography
              variant="h4"
              gutterBottom
              sx={{
                color: '#fff',
                fontWeight: 600,
                fontSize: { xs: '1.3rem', sm: '1.7rem', md: '2.1rem', xl: '2.7rem' },
              }}
            >
              {steps[activeStep].label}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#fff',
                maxWidth: { xs: '100%', md: 700, xl: 850 },
                mx: 'auto',
                fontSize: { xs: '0.97rem', md: '1.09rem', xl: '1.2rem' },
              }}
            >
              {steps[activeStep].description}
            </Typography>
          </Box>
        </Paper>
      </Fade>

      <IconButton
        onClick={prevStep}
        sx={{
          position: 'absolute',
          top: '50%',
          left: { xs: 6, md: 24, xl: 60 },
          transform: 'translateY(-50%)',
          backgroundColor: 'white',
          zIndex: 2,
          boxShadow: 2,
          '&:hover': { backgroundColor: '#f0f0f0' },
        }}
      >
        <KeyboardArrowLeft />
      </IconButton>

      <IconButton
        onClick={nextStep}
        sx={{
          position: 'absolute',
          top: '50%',
          right: { xs: 6, md: 24, xl: 60 },
          transform: 'translateY(-50%)',
          backgroundColor: 'white',
          zIndex: 2,
          boxShadow: 2,
          '&:hover': { backgroundColor: '#f0f0f0' },
        }}
      >
        <KeyboardArrowRight />
      </IconButton>

      <MobileStepper
        steps={maxSteps}
        position="static"
        activeStep={activeStep}
        sx={{
          position: 'absolute',
          bottom: { xs: 24, md: 50 },
          left: 0,
          right: 0,
          justifyContent: 'center',
          backgroundColor: 'transparent',
        }}
        nextButton={<></>}
        backButton={<></>}
      />
    </Box>
  );
};

export default IntroSection;
